/* ============================================================
   request-a-part.js — /pages/request-a-part form behavior.

   Progressive enhancement over a plain <form>:
     - Pre-fills "what part" from ?q= and the Y/M/M fields from
       ?year/?make/?model, and reveals the matching acknowledgment
       note (from-search vs from-YMM).
     - Intercepts submit → one fetch() POST (text/plain simple request,
       see CORS note below) to the endpoint (data-endpoint on the form)
       → mutually-exclusive success / error states.
     - Disables the submit button while sending (no double-submit);
       re-enables on any validation / network error so they can retry.
     - No-JS fallback: the form's own action (/pages/contact) handles
       submit when this script doesn't run.

   Endpoint contract (api/src/functions/submit-part-request.js):
     POST { part, partnumber, year, make, model, notes, email, name,
            phone, company (honeypot), source, q } → { ok }.

   Loaded only at /pages/request-a-part via the section's asset_url tag.
   ============================================================ */
(function () {
  'use strict';

  function init() {
    var form = document.querySelector('[data-part-request-form]');
    if (!form) return;

    var root = form.closest('.request-page') || document;
    var successEl = root.querySelector('[data-part-request-success]');
    var errorEl = form.querySelector('[data-part-request-error]');
    var submitBtn = form.querySelector('[type="submit"]');
    var submitLabel = submitBtn ? submitBtn.textContent : '';
    var params = new URLSearchParams(window.location.search);

    // Visibility helpers — toggle INLINE display, not just the [hidden] attr.
    // .req-form-error sets `display:grid`, an author rule that overrides the UA
    // `[hidden]{display:none}`, so `.hidden` alone never hides the error banner
    // (that's why success + the error were rendering together). An inline style
    // beats the class rule, keeping the two states mutually exclusive. (A CSS
    // `[hidden]` rule would be the deeper fix; kept in JS per the change scope.)
    function hide(el) { if (el) { el.hidden = true; el.style.display = 'none'; } }
    function show(el) { if (el) { el.hidden = false; el.style.display = ''; } }
    function enableSubmit() {
      if (submitBtn) { submitBtn.disabled = false; submitBtn.textContent = submitLabel; }
    }

    // Clear any default / server-rendered status on load.
    hide(errorEl);
    hide(successEl);

    var get = function (n) { return (params.get(n) || '').trim(); };
    var setVal = function (sel, val) {
      var el = form.querySelector(sel);
      if (el && val && !el.value) el.value = val;
    };

    // ── Pre-fill from the deep-link ──
    var q = get('q');
    var pn = get('pn');
    var year = get('year');
    var make = get('make');
    var model = get('model');

    setVal('[name="part"]', q);
    setVal('[name="partnumber"]', pn);
    setVal('[name="year"]', year);
    setVal('[name="make"]', make);
    setVal('[name="model"]', model);

    // ── Reveal the matching acknowledgment note ──
    if (q) {
      var note = root.querySelector('[data-fromsearch-note]');
      var echo = root.querySelector('[data-query-echo]');
      if (echo) echo.textContent = q;
      if (note) note.hidden = false;
    } else if (year || make || model) {
      var ynote = root.querySelector('[data-fromymm-note]');
      var vecho = root.querySelector('[data-vehicle-echo]');
      if (vecho) vecho.textContent = [year, make, model].filter(Boolean).join(' ');
      if (ynote) ynote.hidden = false;
    }

    function showError(msg) {
      enableSubmit(); // every error path lets them retry
      if (!errorEl) return;
      var p = errorEl.querySelector('[data-error-msg]');
      if (p && msg) p.textContent = msg;
      show(errorEl);
      errorEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    function field(name) {
      var el = form.querySelector('[name="' + name + '"]');
      return el ? el.value.trim() : '';
    }

    form.addEventListener('submit', function (e) {
      e.preventDefault();

      // Clear prior status + lock the button up front (no double-submit, shows
      // processing). showError re-enables it on any validation / network error.
      hide(errorEl);
      hide(successEl);
      if (submitBtn) { submitBtn.disabled = true; submitBtn.textContent = 'Sending…'; }

      var part = field('part');
      var email = field('email');
      if (!part) { showError('Tell us the part you’re after.'); return; }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        showError('Add an email so Hubes can reply.');
        return;
      }

      var payload = {
        part: part,
        partnumber: field('partnumber'),
        year: field('year'),
        make: field('make'),
        model: field('model'),
        notes: field('notes'),
        email: email,
        name: field('name'),
        phone: field('phone'),
        company: field('company'), // honeypot
        source: get('source') || 'request-a-part-page',
        q: q,
      };

      var endpoint = form.dataset.endpoint;

      // text/plain to dodge the SWA preflight gotcha — CORS decision record
      // docs/260619_part-request-cors-app-proxy-decision.md / GH #776 (App Proxy
      // is the long-term fix). application/json makes this a NON-simple request →
      // an OPTIONS preflight, which Azure SWA answers with a bare 204 (no
      // Access-Control-Allow-Origin) → the browser blocks the POST. Keeping it a
      // CORS "simple request" (text/plain body, NO other custom headers) skips
      // the preflight; the POST's own CORS headers then satisfy the browser.
      fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain' },
        body: JSON.stringify(payload),
      })
        .then(function (res) {
          return res.json().catch(function () { return {}; }).then(function (body) {
            return { ok: res.ok, status: res.status, body: body };
          });
        })
        .then(function (r) {
          if (r.ok) {
            // Mutually exclusive: clear the error AND the prefill/vehicle
            // acknowledgment notes when success renders.
            hide(errorEl);
            hide(root.querySelector('[data-fromsearch-note]'));
            hide(root.querySelector('[data-fromymm-note]'));
            hide(form);
            show(successEl);
            if (successEl) successEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
            // Button intentionally stays disabled — the form is gone.
          } else if (r.status === 429) {
            showError('That’s a lot of requests in a short window — give it a few minutes and try again.');
          } else {
            showError((r.body && r.body.error) || 'Something went wrong — please try again.');
          }
        })
        .catch(function () {
          showError('Couldn’t reach us just now — please try again, or call the shop.');
        });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
